import { useClipboard } from '@chakra-ui/react';
import { Avatar } from '@foam/buoy/src/components/Avatar/Avatar';
import { Badge } from '@foam/buoy/src/components/Badge/Badge';
import { Checkbox } from '@foam/buoy/src/components/Checkbox/Checkbox';
import {
  DataTable,
  useDataTable,
} from '@foam/buoy/src/components/DataTable/DataTable';
import { SortableHeader } from '@foam/buoy/src/components/DataTable/SortableHeader';
import {
  useColumnHighlight,
  useEndlessPagination,
  useRowExpansion,
  useRowSelection,
  useSorting,
} from '@foam/buoy/src/components/DataTable/plugins';
import { dtColumnHelper } from '@foam/buoy/src/components/DataTable/types';
import { ColorIcon } from '@foam/buoy/src/components/Icon/ColorIcon';
import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { IconButton } from '@foam/buoy/src/components/IconButton/IconButton';
import { Pill } from '@foam/buoy/src/components/Pill/Pill';
import { Tooltip } from '@foam/buoy/src/components/Tooltip/Tooltip';
import { cn } from '@foam/buoy/src/lib/utils';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IClientApiResponse,
  mapCreatorsFromApiModelToViewModel,
} from '~/clientApi';
import { sortDirections, sortFields } from '~/clientApi/agencies.model';
import { Creator } from '~/clientApi/creator.model';
import { SavedList } from '~/clientApi/savedLists.model';
import { routes } from '~/core/router';
import { getMainPlatformData } from '~/core/utils';
import { useGetCreatorLocation } from '~/hooks/useGetCreatorLocation';
import { useSavedListsStore } from '~/screens/MyAgency/store';
import { ICreator, ManagerShortType } from '~/types';
import { formatAbbr } from '~/utils';

import { useCreatorActionModal } from '../CreatorActionModal/CreatorActionModal.context';
import { TalentDropdownMenuV2 } from '../TalentDropdownMenuV2/TalentDropdownMenuV2';
import WebLinks from '../WebLinks';
import { Actions } from './components/Actions';
import { CustomSortableHeader } from './components/CustomSortableHeader';
import { Note } from './components/Note';
import { hasUnicodeStyling } from './utils';

type CreateColumnsProps = {
  isShared?: boolean;
  list?: SavedList;
};

type CreatorTableColIds =
  | '__select__'
  | 'name'
  | 'bioAgency'
  | 'categoryAgency'
  | 'age'
  | 'gender'
  | 'location'
  | 'weblinks'
  | 'ig.followers'
  | 'tt.followers'
  | 'yt.followers'
  | 'sc.followers'
  | 'links'
  | 'manager'
  | 'actions';

const [columnHelper, colIds] = dtColumnHelper<ICreator, CreatorTableColIds>();

const createColumns = ({ isShared = false, list }: CreateColumnsProps) => {
  const columns: ColumnDef<ICreator>[] = [
    columnHelper.accessor('name', {
      id: colIds('name'),
      header: ({ column }) => (
        <div className="flex justify-center">
          <SortableHeader
            column={column}
            data-track-id="foam-list-view-sortby-name"
          >
            Talent name
          </SortableHeader>
        </div>
      ),
      cell: (cell) => {
        const {
          avatar = '',
          name = '',
          handle = '',
          email = '',
        } = getMainPlatformData(cell.row.original);
        return (
          <div className="flex items-center space-x-3">
            <Avatar
              src={avatar}
              size="md"
              name={hasUnicodeStyling(name) ? undefined : name}
            />
            {!cell.row.original.slug ? (
              <div className="font-[--wb-text-weight-500]">
                {name || handle || email}
              </div>
            ) : (
              <Link
                className="font-[--wb-text-weight-500]"
                href={
                  isShared
                    ? routes.foamIOCreatorProfile(cell.row.original.slug)
                    : routes.profileOverview(cell.row.original.slug)
                }
              >
                {name || handle || email}
              </Link>
            )}
          </div>
        );
      },
      size: 200,
    }),
    columnHelper.accessor('bioAgency', {
      id: colIds('bioAgency'),
      header: 'Biography',
      cell: (info) => {
        const bio = info.getValue();

        if (bio) {
          return (
            <Tooltip content={bio}>
              <div className="max-w-xs truncate text-14">{bio}</div>
            </Tooltip>
          );
        }

        return <span>-</span>;
      },
      enableSorting: false,
      size: 300,
    }),
    columnHelper.accessor('categoryAgency', {
      id: colIds('categoryAgency'),
      header: 'Verticals',
      cell: (info) => {
        const verticalsString = info.getValue();
        const verticals = verticalsString?.split(',').filter(Boolean) ?? [];
        return (
          <div className="flex flex-wrap gap-1">
            {verticals.length ? (
              verticals.map((vertical, index) => (
                <Pill key={index}>{vertical}</Pill>
              ))
            ) : (
              <span>-</span>
            )}
          </div>
        );
      },
      enableSorting: false,
      size: 220,
    }),
    columnHelper.accessor('age', {
      id: colIds('age'),
      header: ({ column }) => {
        return (
          <SortableHeader
            column={column}
            data-track-id="foam-list-view-sortby-age"
          >
            Age
          </SortableHeader>
        );
      },
      cell: (info) => {
        const age = info.getValue();

        if (typeof age === 'number') {
          return (
            <span className="block text-center font-mono text-root-size-body-md">
              {age}
            </span>
          );
        }

        return (
          <span className="block text-center font-mono text-root-size-body-md">
            -
          </span>
        );
      },
      size: 40,
    }),
    columnHelper.accessor('genderAgency', {
      id: colIds('gender'),
      header: 'Gender',
      cell: (info) => {
        const gender = info.getValue();
        return gender ? (
          <Pill>
            {gender === 'Non Binary' ? 'NB' : gender.charAt(0).toUpperCase()}
          </Pill>
        ) : (
          <span>-</span>
        );
      },
      size: 40,
    }),
    columnHelper.accessor('location', {
      id: colIds('location'),
      header: ({ column }) => {
        return (
          <SortableHeader
            column={column}
            data-track-id="foam-list-view-sortby-location"
          >
            Location
          </SortableHeader>
        );
      },
      cell: (cell) => {
        const creator = cell.row.original;
        return <ListViewLocation creator={creator} />;
      },
      size: 200,
    }),
    columnHelper.accessor('platforms.instagram.followersCount', {
      id: colIds('ig.followers'),
      header: ({ column }) => {
        return (
          <div className="flex justify-center">
            <CustomSortableHeader
              column={column}
              className="gap-0"
              data-track-id="foam-list-view-sortby-instagram"
            >
              <ColorIcon name="Instagram" className="text-16" />
            </CustomSortableHeader>
          </div>
        );
      },
      cell: (info) => {
        const count = info.getValue();
        return (
          <span className="block text-center font-mono text-root-size-body-md">
            {formatAbbr(count)}
          </span>
        );
      },
      size: 40,
    }),
    columnHelper.accessor('platforms.tiktok.followersCount', {
      id: colIds('tt.followers'),
      header: ({ column }) => {
        return (
          <div className="flex justify-center">
            <CustomSortableHeader
              column={column}
              className="gap-0"
              data-track-id="foam-list-view-sortby-tiktok"
            >
              <ColorIcon name="TikTok" className="text-16" />
            </CustomSortableHeader>
          </div>
        );
      },
      cell: (info) => {
        const count = info.getValue();
        return (
          <span className="block text-center font-mono text-root-size-body-md">
            {formatAbbr(count)}
          </span>
        );
      },
      size: 40,
    }),
    columnHelper.accessor('platforms.youtube.followersCount', {
      id: colIds('yt.followers'),
      header: ({ column }) => {
        return (
          <div className="flex justify-center">
            <CustomSortableHeader
              column={column}
              className="gap-0"
              data-track-id="foam-list-view-sortby-youtube"
            >
              <ColorIcon name="YouTube" className="text-16" />
            </CustomSortableHeader>
          </div>
        );
      },
      cell: (info) => {
        const count = info.getValue();
        return (
          <span className="block text-center font-mono text-root-size-body-md">
            {formatAbbr(count)}
          </span>
        );
      },
      size: 40,
    }),
    columnHelper.accessor('platforms.snapchat.followersCount', {
      id: colIds('sc.followers'),
      header: ({ column }) => {
        return (
          <div className="flex justify-center">
            <CustomSortableHeader
              column={column}
              className="gap-0"
              data-track-id="foam-list-view-sortby-snap"
            >
              <ColorIcon name="Snap" className="text-16" />
            </CustomSortableHeader>
          </div>
        );
      },
      cell: (info) => {
        const count = info.getValue();
        return (
          <span className="block text-center font-mono text-root-size-body-md">
            {formatAbbr(count)}
          </span>
        );
      },
      size: 40,
    }),
    columnHelper.accessor('webLinks', {
      id: colIds('weblinks'),
      header: 'Links',
      cell: (info) => {
        const webLinks = info.getValue();
        if (isEmpty(webLinks)) {
          return <span>-</span>;
        }
        return (
          <div className="flex flex-wrap items-center">
            <WebLinks links={webLinks} />
          </div>
        );
      },
    }),
    columnHelper.accessor('manager', {
      id: colIds('manager'),
      header: 'Manager',
      cell: (info) => {
        const manager = info.getValue();
        if (isEmpty(manager)) {
          return <span>-</span>;
        }
        return <ListViewManager manager={manager} />;
      },
    }),
  ];

  if (list?.category === 'list') {
    columns.unshift(
      columnHelper.display({
        id: '__select__',
        cell: ({ row }) => (
          <Checkbox
            aria-label="Select row"
            checked={row.getIsSelected()}
            data-test-id="foam-list-view-select-talent"
            onChange={(e) => row.toggleSelected(e.target.checked)}
          />
        ),
        size: 32,
      }),
    );
  }

  if (!isShared) {
    columns.push(
      columnHelper.display({
        id: colIds('actions'),
        cell: (cell) => (
          <ListViewActions creator={cell.row.original} list={list} />
        ),
        size: 40,
      }),
    );
  }

  return columns;
};

type ListViewProps = {
  infiniteQueryReturn: UseInfiniteQueryResult<
    IClientApiResponse<Creator>,
    unknown
  >;
  isShared?: boolean;
  list?: SavedList;
  setSortingOrder: (field: sortFields, direction: sortDirections) => void;
  sortDirection: sortDirections;
  sortField: sortFields;
};

export const ListViewV2: React.FC<ListViewProps> = ({
  infiniteQueryReturn,
  isShared = false,
  list,
  setSortingOrder,
  sortDirection = 'asc',
  sortField = 'name',
}) => {
  const { creators: selectedCreators, setCreators } = useSavedListsStore();
  const { data } = infiniteQueryReturn;
  const creators = data?.pages.flatMap((page) => page.results) ?? [];
  const mappedCreators = mapCreatorsFromApiModelToViewModel(creators);

  const initialRowSelection = useMemo(() => {
    // Set initial row selection based on selected creators
    return creators?.reduce(
      (acc: { [key: number]: boolean }, creator, index) => {
        if (selectedCreators.some(({ id }: ICreator) => id === creator.id)) {
          acc[index] = true;
        }
        return acc;
      },
      {},
    );
  }, [creators, selectedCreators]);

  const rowExpansion = useMemo(() => {
    return creators?.reduce(
      (acc: { [key: number]: boolean }, creator, index) => {
        if (creator.notes) {
          acc[index] = true;
        }
        return acc;
      },
      {},
    );
  }, [creators]);

  const { plugin: columnHighlightPlugin } = useColumnHighlight<ICreator>({
    initialColumnHighlight: [colIds('ig.followers'), colIds('yt.followers')],
  });

  const {
    plugin: rowSelectionPlugin,
    actions: { resetRowSelection },
  } = useRowSelection<ICreator>({
    initialRowSelection,
    onRowSelectionChange: (rowSelection) => {
      const checkedCreatorIndex = Object.keys(rowSelection).map(Number);
      if (checkedCreatorIndex.length) {
        const updatedCreators = creators.filter((_creator, index) =>
          checkedCreatorIndex.includes(index),
        );
        return setCreators(updatedCreators as unknown as ICreator[]);
      }
      setCreators([]);
    },
  });

  const { plugin: paginationPlugin } = useEndlessPagination({
    infiniteQueryReturn,
    getItemsFromPage: (page) =>
      mapCreatorsFromApiModelToViewModel(page.results),
  });

  const { plugin: sortingPlugin, sorting } = useSorting<ICreator>({
    initialSorting: [{ id: sortField, desc: sortDirection === 'desc' }],
  });

  const { plugin: rowExpansionPlugin } = useRowExpansion<ICreator>({
    ExpandedContent: ({ actions }) => <Note actions={actions} />,
    isSticky: true,
  });

  const columns = useMemo(
    () =>
      createColumns({
        list,
        isShared,
      }),
    [list, isShared],
  );

  useEffect(() => {
    if (sorting.length) {
      setSortingOrder(sorting[0].id as any, sorting[0].desc ? 'desc' : 'asc');
    }
  }, [sorting, setSortingOrder]);

  useEffect(() => {
    // Reset row selection when no creators are selected
    if (!selectedCreators.length) {
      resetRowSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCreators]);

  const dataTable = useDataTable<ICreator>({
    tableOptions: {
      state: {
        expanded: rowExpansion,
      },
      data: mappedCreators,
      columns,
    },
    columnPinning: {
      left: ['select', 'name'],
      right: ['actions'],
    },
    plugins: [
      columnHighlightPlugin,
      paginationPlugin,
      rowExpansionPlugin,
      rowSelectionPlugin,
      sortingPlugin,
    ],
  });

  return (
    <>
      <div
        className={clsx({
          'ml-[-24px]': list?.category === 'list',
        })}
      >
        <DataTable dataTable={dataTable} />
      </div>

      {!isShared && <Actions list={list} />}
    </>
  );
};

function ListViewManager({ manager }: { manager: ManagerShortType }) {
  const [isCopied, setIsCopied] = useState(false);
  const { onCopy } = useClipboard(manager.email);

  const handleCopy = useCallback(() => {
    onCopy();
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  }, [onCopy]);

  if (isCopied) {
    return (
      <Badge variant="success" size="sm">
        <Icon name="Checkmark" />
        Email copied!
      </Badge>
    );
  }

  return (
    <div className="group relative min-w-[200px] max-w-[200px]">
      <div className="group-hover:hidden">{manager.name}</div>
      <button
        data-test-id="fs-list-copy-manager-email"
        className={cn(
          'w-full hidden whitespace-nowrap',
          'group-hover:flex items-center gap-1',
          'font-[--wb-text-weight-500]',
          'overflow-hidden',
        )}
        onClick={handleCopy}
      >
        <Icon name="Copy" />
        <span className="truncate">{manager.email}</span>
      </button>
    </div>
  );
}

function ListViewActions({
  creator,
  list,
}: {
  creator: ICreator;
  list?: SavedList;
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isEditable = list?.category === 'list';

  const { setCreators, setIsRemoveFromListModalOpen, setIsEditNoteModalOpen } =
    useCreatorActionModal();

  const handleRemoveCreator = useCallback(() => {
    setCreators([creator]);
    setIsRemoveFromListModalOpen(true);
  }, [creator, setCreators, setIsRemoveFromListModalOpen]);

  const handleEditNote = useCallback(() => {
    setCreators([creator]);
    setIsEditNoteModalOpen(true);
  }, [creator, setCreators, setIsEditNoteModalOpen]);

  return (
    <div
      className={cn(
        'flex justify-end items-center opacity-0 group-hover:opacity-100 transition-opacity duration-150 gap-2',
        isDropdownOpen && 'opacity-100',
      )}
    >
      {isEditable && (
        <IconButton
          variant="danger"
          icon="Trash"
          onClick={handleRemoveCreator}
          size="lg"
          data-test-id="foam-list-view-remove-icon"
          data-track-id="foam-list-view-remove-icon"
        />
      )}

      <TalentDropdownMenuV2
        creator={creator}
        list={list}
        onOpenChange={setIsDropdownOpen}
        onRemoveFromList={isEditable ? handleRemoveCreator : undefined}
        onEditNote={isEditable ? handleEditNote : undefined}
      />
    </div>
  );
}

function ListViewLocation({ creator }: { creator?: ICreator }) {
  const { getLocation } = useGetCreatorLocation();

  return creator ? (
    <span className="text-14">{getLocation(creator)}</span>
  ) : (
    <span>-</span>
  );
}
